import { Link } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { ThemeToggle } from '../components/layout/ThemeToggle';
import { SettingsSection } from '../components/templates/SettingsSection';
import { PermissionGate } from '../components/access/PermissionGate';
import { CardActions, PageHeader } from '../components/ui';
import { useCapabilities } from '../lib/capabilities';
import { useDiscovery } from '../lib/discovery';
import { settingsRegistry, resolveEntries } from '../lib/registries';
import { moduleSettingsEntries } from '../lib/host';

export function SettingsPage(): JSX.Element {
  const { hasPermission, featureState } = useCapabilities();
  const { openTeaser } = useDiscovery();

  // Settings rows contributed via the settings registry slot (P16 §4, story
  // 2-6): a feature registers its row; this page never grows bespoke sections
  // for them. Locked rows (plan-gated) render with a lock affordance that opens
  // the discovery teaser (2-1) → "Request access" (2-2) instead of a dead link.
  //
  // Story 3-3b: `moduleSettingsEntries()` is merged in, exactly as Sidebar.tsx
  // merges `moduleNavEntries()`. Every OTHER consumer of the settings registry
  // already did this — AppShell (breadcrumbs), GatedRoute (route containment)
  // and useCommandSearch (Cmd+K) all read
  // `[...settingsRegistry, ...moduleSettingsEntries()]` — and this page, the one
  // that actually RENDERS the rows, was the single place that did not. It went
  // unnoticed because the only module shipped so far (billing) contributes a
  // `nav` row and no `settings` row, so nothing exercised the slot; the
  // inspectionTypes module is the first, and without this line its row would be
  // reachable by URL and by Cmd+K but invisible on the page that exists to list
  // it. This is host wiring being completed, not a bespoke section: the page
  // still grows no per-feature code.
  const registryRows = resolveEntries([...settingsRegistry, ...moduleSettingsEntries()], {
    hasPermission,
    featureState,
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Workspace settings" description="Tune the DefectSnap experience for your team." />

      <PermissionGate need="org:members:manage">
        <SettingsSection
          title="Access & roles"
          description="Manage members, roles, and permissions for your organization."
          action={
            <Link to="/app/settings/access" className="btn-glass btn-secondary focus-ring text-sm font-semibold">
              Open access console
            </Link>
          }
        />
      </PermissionGate>

      {registryRows.map((row) => (
        <SettingsSection
          key={row.key}
          title={row.label}
          {...(row.description ? { description: row.description } : {})}
          action={
            row.locked ? (
              <CardActions
                actions={[
                  {
                    id: 'locked-learn-more',
                    label: 'Locked — learn more',
                    variant: 'secondary',
                    icon: <LockClosedIcon className="h-4 w-4" aria-hidden />,
                    onClick: () => {
                      if (row.featureKey) openTeaser(row.featureKey, { name: row.label });
                    },
                  },
                ]}
              />
            ) : (
              <Link to={row.to ?? '#'} className="btn-glass btn-secondary focus-ring text-sm font-semibold">
                Open
              </Link>
            )
          }
        />
      ))}

      <SettingsSection title="Appearance" description="Switch theme for your workflow." action={<ThemeToggle />} />

      <SettingsSection
        title="Report templates"
        description="Configure inspection report layouts by uploading samples or defining JSON schemas."
        action={
          <Link to="/app/report-presets" className="btn-glass btn-secondary focus-ring text-sm font-semibold">
            Manage report presets
          </Link>
        }
      />
    </div>
  );
}
